import * as React from "react";
import { cachedTracks, searchLocalTracks, searchRemoteTracks, type TrackResult } from "./track-search";

export function TrackSearch({
  authenticated,
  actionLabel = "Choose",
  placeholder = "Find a song, artist, or album",
  onRequireAuth,
  onSelect,
}: {
  authenticated: boolean;
  actionLabel?: string;
  placeholder?: string;
  onRequireAuth: () => void;
  onSelect: (track: TrackResult) => void | Promise<void>;
}) {
  const [query, setQuery] = React.useState("");
  const [tracks, setTracks] = React.useState<TrackResult[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [loadingRemote, setLoadingRemote] = React.useState(false);
  const [error, setError] = React.useState("");
  const requestRef = React.useRef(0);

  React.useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setTracks([]);
      setActiveIndex(-1);
      setError("");
      return;
    }
    const cached = cachedTracks(trimmed);
    if (cached) setTracks(cached);
    const requestId = ++requestRef.current;
    const localController = new AbortController();
    const remoteController = new AbortController();
    setError("");
    void searchLocalTracks(trimmed, localController.signal)
      .then((local) => {
        if (requestRef.current !== requestId) return;
        setTracks(local);
        setActiveIndex(local.length ? 0 : -1);
        if (!authenticated) return;
        setLoadingRemote(true);
        window.setTimeout(() => {
          if (requestRef.current !== requestId) return;
          void searchRemoteTracks(trimmed, local, remoteController.signal)
            .then((merged) => {
              if (requestRef.current === requestId) setTracks(merged);
            })
            .catch((cause) => {
              if (cause?.name !== "AbortError" && requestRef.current === requestId) {
                setError(cause?.message || "Spotify search is unavailable");
              }
            })
            .finally(() => {
              if (requestRef.current === requestId) setLoadingRemote(false);
            });
        }, 250);
      })
      .catch((cause) => {
        if (cause?.name !== "AbortError" && requestRef.current === requestId) {
          setError(cause?.message || "Search is unavailable");
        }
      });
    return () => {
      localController.abort();
      remoteController.abort();
    };
  }, [authenticated, query]);

  const choose = React.useCallback((track: TrackResult) => {
    if (!authenticated) {
      onRequireAuth();
      return;
    }
    void onSelect(track);
  }, [authenticated, onRequireAuth, onSelect]);

  return (
    <div className="track-search">
      <label className="search-field">
        <span className="sr-only">Search Spotify songs</span>
        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m21 21-4.3-4.3m2.3-5.2a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" /></svg>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((index) => Math.min(tracks.length - 1, index + 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((index) => Math.max(0, index - 1));
            } else if (event.key === "Enter" && tracks[activeIndex]) {
              event.preventDefault();
              choose(tracks[activeIndex]);
            } else if (event.key === "Escape") {
              setTracks([]);
            }
          }}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={tracks.length > 0}
          aria-controls="track-search-results"
          aria-activedescendant={tracks[activeIndex] ? `track-option-${tracks[activeIndex].spotifyId}` : undefined}
          autoComplete="off"
        />
        {loadingRemote && <span className="search-pulse" aria-label="Checking Spotify" />}
      </label>
      {query.trim().length === 1 && <p className="field-hint">Type one more character.</p>}
      {error && <p className="field-error" role="status">{error}</p>}
      {tracks.length > 0 && (
        <div id="track-search-results" className="search-results" role="listbox">
          {tracks.map((track, index) => (
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              id={`track-option-${track.spotifyId}`}
              className={`search-result ${index === activeIndex ? "is-active" : ""}`}
              key={track.spotifyId}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(track)}
            >
              <span className="track-art mini-art">
                {track.artworkUrl ? <img src={track.artworkUrl} alt="" width="48" height="48" loading="lazy" /> : <span>♪</span>}
              </span>
              <span className="result-copy">
                <strong>{track.title}</strong>
                <small>{track.artist}{track.album ? ` · ${track.album}` : ""}</small>
              </span>
              <span className={`source-chip ${track.source}`}>{track.source === "local" ? "Jamband" : "Spotify"}</span>
              <span className="result-action">{actionLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
